import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import HeritageMark from "@/components/common/HeritageMark";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Rates", href: "#rates" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", href: "#contact" },
];

export default function CareNav({ site }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={`care-nav-${site.key}`}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-[#D5DACF]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link
          to={pathname}
          data-testid="care-nav-logo"
          className="flex items-center gap-3 group"
        >
          <HeritageMark size={36} />
          <div className="font-serif-display leading-none">
            <div className="text-xl md:text-2xl text-[#1A2621]">Heritage</div>
            <div className="text-[11px] uppercase tracking-[0.26em] text-[#4A5D54] mt-1">
              {site.region}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) =>
            n.to ? (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`care-nav-link-${n.to.slice(1)}`}
                className="text-sm text-[#1A2621] hover:text-[#C86A51] transition-colors"
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.href}
                href={n.href}
                data-testid={`care-nav-${n.href.slice(1)}`}
                className="text-sm text-[#1A2621] hover:text-[#C86A51] transition-colors"
              >
                {n.label}
              </a>
            ),
          )}
          <a
            href="#contact"
            data-testid="care-nav-cta"
            className="inline-flex items-center gap-2 bg-[#284A3B] hover:bg-[#1C352A] text-[#FDFBF7] rounded-full px-6 py-2.5 text-sm font-medium transition-colors"
          >
            Request Care
          </a>
        </nav>

        <button
          aria-label="Menu"
          data-testid="care-mobile-toggle"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden w-10 h-10 rounded-full border border-[#D5DACF] bg-white/70 flex items-center justify-center"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="care-mobile-menu"
          className="lg:hidden bg-[#FDFBF7] border-t border-[#D5DACF] px-6 py-6 flex flex-col gap-5"
        >
          {NAV.map((n) =>
            n.to ? (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="text-base text-[#1A2621]"
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="text-base text-[#1A2621]"
              >
                {n.label}
              </a>
            ),
          )}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="bg-[#284A3B] text-[#FDFBF7] rounded-full px-6 py-3 text-sm font-medium text-center"
          >
            Request Care
          </a>
        </div>
      )}
    </header>
  );
}
